import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { Platform } from '../utils/platform';

const BACKGROUND_TIME_KEY = 'app_background_time';

/**
 * Hook to handle app resume from background on iOS/Android.
 * Calls the provided callback when the app becomes active after being in background.
 * 
 * Uses localStorage to persist background time across WebView terminations.
 * This handles the case where iOS terminates the WebView after long background idle
 * and the in-memory ref would be lost.
 * 
 * @param onResume - Callback to execute when app resumes from background
 * @param minBackgroundTime - Minimum time in ms the app must be backgrounded before triggering (default: 30 seconds)
 */
export const useAppResume = (
  onResume: () => void,
  minBackgroundTime: number = 30000
) => {
  const onResumeRef = useRef(onResume);
  const hasCheckedColdStartRef = useRef(false);
  
  // Keep callback ref updated
  useEffect(() => {
    onResumeRef.current = onResume;
  }, [onResume]);

  // Check for cold start (WebView was terminated while in background)
  useEffect(() => {
    if (!Platform.isNative || hasCheckedColdStartRef.current) {
      return;
    }
    
    hasCheckedColdStartRef.current = true;
    
    const storedTime = localStorage.getItem(BACKGROUND_TIME_KEY);
    if (storedTime) {
      const backgroundTime = parseInt(storedTime, 10);
      const elapsed = Date.now() - backgroundTime;
      
      // Clear the stored time
      localStorage.removeItem(BACKGROUND_TIME_KEY);
      
      // Trigger refresh if backgrounded for longer than threshold
      if (elapsed >= minBackgroundTime) {
        // Small delay to ensure app is fully initialized
        setTimeout(() => {
          onResumeRef.current();
        }, 100);
      }
    }
  }, [minBackgroundTime]);

  useEffect(() => {
    // Only set up listener on native platforms
    if (!Platform.isNative) {
      return;
    }

    const setupListener = async () => {
      try {
        const listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            // App going to background - persist time to localStorage
            const now = Date.now();
            localStorage.setItem(BACKGROUND_TIME_KEY, now.toString());
          } else {
            // App becoming active - check localStorage for background time
            const storedTime = localStorage.getItem(BACKGROUND_TIME_KEY);
            if (storedTime) {
              const backgroundTime = parseInt(storedTime, 10);
              const elapsed = Date.now() - backgroundTime;
              
              // Clear the stored time
              localStorage.removeItem(BACKGROUND_TIME_KEY);
              
              // Only trigger refresh if backgrounded for longer than threshold
              if (elapsed >= minBackgroundTime) {
                onResumeRef.current();
              }
            }
          }
        });

        return () => {
          listener.remove();
        };
      } catch (error) {
        console.error('[AppResume] Failed to set up app state listener:', error);
      }
    };

    const cleanup = setupListener();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [minBackgroundTime]);
};

export default useAppResume;
